export type Currency = 'CHF' | 'EUR' | 'USD';

export interface CurrencyConfig {
  code: Currency;
  symbol: string;
  name: string;
  prices: {
    lite: number;
    starter: number;
    boost: number;
  };
}

export const CURRENCY_CONFIG: Record<Currency, CurrencyConfig> = {
  CHF: {
    code: 'CHF',
    symbol: 'CHF',
    name: 'Swiss Franc',
    prices: {
      lite: 340,
      starter: 780,
      boost: 1440,
    },
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    name: 'Euro',
    prices: {
      lite: 370,
      starter: 840,
      boost: 1560,
    },
  },
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    prices: {
      lite: 430,
      starter: 980,
      boost: 1790,
    },
  },
};

export const DEFAULT_CURRENCY: Currency = 'CHF';

/**
 * Map ISO 3166-1 alpha-2 country codes to currency.
 */
export const COUNTRY_TO_CURRENCY: Record<string, Currency> = {
  CH: 'CHF',
  LI: 'CHF',
  // EUR zone
  DE: 'EUR',
  AT: 'EUR',
  FR: 'EUR',
  IT: 'EUR',
  ES: 'EUR',
  PT: 'EUR',
  NL: 'EUR',
  BE: 'EUR',
  LU: 'EUR',
  IE: 'EUR',
  FI: 'EUR',
  GR: 'EUR',
  CY: 'EUR',
  SK: 'EUR',
  SI: 'EUR',
  EE: 'EUR',
  LV: 'EUR',
  LT: 'EUR',
  HR: 'EUR',
  MC: 'EUR',
  SM: 'EUR',
  // Non-EUR European (display in EUR for simplicity)
  PL: 'EUR',
  CZ: 'EUR',
  HU: 'EUR',
  RO: 'EUR',
  BG: 'EUR',
  DK: 'EUR',
  SE: 'EUR',
  NO: 'EUR',
  // USD zone
  US: 'USD',
  GB: 'USD',
  CA: 'USD',
  AU: 'USD',
  NZ: 'USD',
};

/**
 * Get currency for a country code. Defaults to EUR.
 */
export function getCurrencyForCountry(countryCode: string): Currency {
  const upper = countryCode.toUpperCase();
  return COUNTRY_TO_CURRENCY[upper] ?? 'EUR';
}

/**
 * Format a price amount with the given currency.
 * CHF 340 / 370 € / $430
 */
/**
 * Groups thousands with a fixed separator. toLocaleString is avoided because
 * Node and browsers ship different ICU data (de-CH: "5’000" vs "5'000"),
 * which breaks hydration of server-rendered prices.
 */
export function groupThousands(
  amount: number,
  separator: string,
  decimalSeparator = '.'
): string {
  // Match toLocaleString's default of at most 3 fraction digits.
  const rounded = Math.round(Math.abs(amount) * 1000) / 1000;
  const [integer, fraction] = String(rounded).split('.');
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  const sign = amount < 0 && rounded !== 0 ? '-' : '';
  return fraction
    ? `${sign}${grouped}${decimalSeparator}${fraction}`
    : `${sign}${grouped}`;
}

export function formatPrice(amount: number, currency: Currency): string {
  const config = CURRENCY_CONFIG[currency];
  if (currency === 'CHF') {
    return `CHF ${groupThousands(amount, '’')}`;
  }
  if (currency === 'EUR') {
    return `${groupThousands(amount, '.', ',')} €`;
  }
  // USD
  return `${config.symbol}${groupThousands(amount, ',')}`;
}

export function getCurrencyConfig(currency: Currency): CurrencyConfig {
  return CURRENCY_CONFIG[currency];
}

export function getPriceForPlan(currency: Currency, plan: 'lite' | 'starter' | 'boost'): number {
  return CURRENCY_CONFIG[currency].prices[plan];
}

/** Validate a raw currency value (e.g. the middleware's x-currency header). */
export function parseCurrency(value: string | null | undefined): Currency {
  return value === 'CHF' || value === 'EUR' || value === 'USD'
    ? value
    : DEFAULT_CURRENCY;
}

/**
 * Placeholder values for FAQ answers that mention prices. Shared by the
 * visible FAQ and the FAQPage JSON-LD so both always show the same text.
 */
export function getFaqPriceParams(currency: Currency): Record<string, string> {
  return { litePrice: formatPrice(getPriceForPlan(currency, 'lite'), currency) };
}
